// frontend/src/services/superAdminApi.js
// ============================================================================
// SERVICE API SUPER-ADMIN
// ============================================================================

import { API_URL } from '../config/apiBase';

// Récupérer le hash de route depuis le backend (normalement dans un .env)
// Pour l'instant on le hardcode, mais tu peux le mettre dans .env
const ADMIN_ROUTE_HASH = '7f3a9c2e'; // À synchroniser avec backend

// ============================================================================
// HELPERS
// ============================================================================

const getAuthHeaders = () => {
  // ✅ ESSAYER D'ABORD LE TOKEN SUPER-ADMIN
  let token = localStorage.getItem('superadmin_token');
  
  // Si pas de token super-admin, essayer le token normal
  if (!token) {
    token = localStorage.getItem('token');
  }
  
  if (!token) {
    console.log('❌ [SuperAdmin] Aucun token trouvé');
  } else {
    console.log('🔑 [SuperAdmin] Token trouvé');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));
    throw new Error(error.message || 'Une erreur est survenue');
  }
  return response.json();
};

// ============================================================================
// API SUPER-ADMIN
// ============================================================================

const superAdminApi = {
  
  // ==========================================================================
  // AUTHENTIFICATION SUPER-ADMIN
  // ==========================================================================
  
  auth: {
    // Login super-admin
    login: async (email, password) => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      return handleResponse(response);
    }
  },
  
  // ==========================================================================
  // TENTATIVES DE CONNEXION
  // ==========================================================================

  connexions: {
    // Journal des tentatives, filtre cote serveur
    liste: async (filtres = {}) => {
      const params = new URLSearchParams();
      Object.entries(filtres).forEach(([cle, valeur]) => {
        if (valeur !== '' && valeur !== null && valeur !== undefined) {
          params.append(cle, valeur);
        }
      });
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/login-attempts?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    },

    // Un compteur par entreprise sur la periode
    resume: async (jours = 30) => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/login-attempts/resume?jours=${jours}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    }
  },

  // ==========================================================================
  // ACTIONS PRIVILEGIEES ET EXPLOITATION
  // ==========================================================================

  actions: {
    liste: async (filtres = {}) => {
      const params = new URLSearchParams();
      Object.entries(filtres).forEach(([cle, valeur]) => {
        if (valeur !== '' && valeur !== null && valeur !== undefined) params.append(cle, valeur);
      });
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/actions-privilegiees?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    }
  },

  requetes: {
    etat: async (tri = 'moyenne', limite = 25) => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/requetes-lentes?tri=${tri}&limite=${limite}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    },
    remettreAZero: async () => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/requetes-lentes/remise-a-zero`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      return response.json();
    }
  },

  administrateurs: {
    liste: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/administrateurs`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    creer: async ({ nom, email }) => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/administrateurs`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ nom, email })
      });
      return handleResponse(response);
    },
    statut: async (id, actif) => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/administrateurs/${id}/statut`,
        { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ actif }) }
      );
      return handleResponse(response);
    },
    reinitialiserDoubleAuth: async (id) => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/administrateurs/${id}/double-auth/reinitialiser`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      return handleResponse(response);
    }
  },

  conformite: {
    etat: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/conformite`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  activite: {
    etat: async (jours = 30) => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/activite?jours=${jours}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  exploitation: {
    etat: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/exploitation`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    migrations: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/migrations`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    sauvegardes: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/sauvegardes`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    appliquerMigrations: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/migrations/appliquer`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  // ==========================================================================
  // STATISTIQUES GLOBALES
  // ==========================================================================
  
  stats: {
    // Stats globales du système
    getGlobalStats: async () => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/stats`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },
  
  // ==========================================================================
  // GESTION DES ENTREPRISES
  // ==========================================================================
  
  companies: {
    
    // Liste toutes les entreprises
    getAll: async () => {
      console.log('📡 [SuperAdmin API] Récupération de toutes les entreprises...');
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    
    // Détails d'une entreprise
    getOne: async (companyId) => {
      console.log(`📡 [SuperAdmin API] Récupération entreprise ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    
    // Créer une entreprise
    create: async (companyData) => {
      console.log('📡 [SuperAdmin API] Création entreprise:', companyData);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(companyData)
      });
      return handleResponse(response);
    },
    
    // Mettre à jour une entreprise
    update: async (companyId, companyData) => {
      console.log(`📡 [SuperAdmin API] Mise à jour entreprise ${companyId}:`, companyData);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(companyData)
      });
      return handleResponse(response);
    },
    
    // Bloquer une entreprise
    block: async (companyId) => {
      console.log(`📡 [SuperAdmin API] Blocage entreprise ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/block`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    
    // Débloquer une entreprise
    unblock: async (companyId) => {
      console.log(`📡 [SuperAdmin API] Déblocage entreprise ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/unblock`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    
    // Supprimer une entreprise (soft delete)
    delete: async (companyId) => {
      console.log(`📡 [SuperAdmin API] Suppression entreprise ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Configurer le rappel de facturation d'une entreprise
    updateBilling: async (companyId, billingData) => {
      console.log(`📡 [SuperAdmin API] Config billing company ${companyId}:`, billingData);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/billing`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(billingData)
      });
      return handleResponse(response);
    },

    // Récupérer les utilisateurs d'une entreprise
    // Dupliquer un client : son offre, ses extensions, son parametrage.
    dupliquer: async (companyId, donnees) => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/dupliquer`,
        { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(donnees) }
      );
      return handleResponse(response);
    },

    getUsers: async (companyId) => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/users`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Changer l'adresse de connexion d'un compte (et son nom au passage).
    changerIdentifiant: async (companyId, userId, { email, name }) => {
      const response = await fetch(
        `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/users/${userId}/identifiant`,
        { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ email, name }) }
      );
      return handleResponse(response);
    },

    // Réinitialiser le mot de passe d'un utilisateur
    resetUserPassword: async (companyId, userId) => {
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/users/${userId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

  },
  
  // ==========================================================================
  // GESTION DES UTILISATEURS D'UNE ENTREPRISE
  // ==========================================================================
  
  users: {
    
    // Liste des utilisateurs d'une entreprise
    getByCompany: async (companyId) => {
      console.log(`📡 [SuperAdmin API] Récupération utilisateurs de ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/users`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    
    // Créer un utilisateur pour une entreprise
    create: async (companyId, userData) => {
      console.log(`📡 [SuperAdmin API] Création utilisateur pour ${companyId}:`, userData);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      return handleResponse(response);
    },
    
    // Réinitialiser le mot de passe d'un utilisateur
    resetPassword: async (userId) => {
      console.log(`📡 [SuperAdmin API] Réinitialisation mot de passe user ${userId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/users/${userId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },

  // ==========================================================================
  // GESTION DES LICENCES  ET VERSION HORAPILOT
  // ==========================================================================



versions: {

  getAll: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/versions`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getCurrent: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/versions/current`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getEntreprises: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/versions/entreprises`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/versions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/versions/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/versions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  deployGlobal: async (version_app, notes) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licences/version/global`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ version_app, notes }),
    });
    return handleResponse(response);
  },

},


  
 licences: {

  // ── Existant ──────────────────────────────────────────────────────────────

  getPlans: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-plans`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLicence: async (companyId) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/licence`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateLimits: async (companyId, limits) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/licence/limits`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(limits)
    });
    return handleResponse(response);
  },

  upgrade: async (companyId, type_licence) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/licence/upgrade`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ type_licence })
    });
    return handleResponse(response);
  },

  getHistory: async (companyId) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/licence/history`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ── Nouveau — LicenceManager ──────────────────────────────────────────────

  getAll: async () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licences`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  setFlag: async (licenceId, flagKey, enabled) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licences/${licenceId}/flag`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ flag_key: flagKey, enabled }),
    }).then(handleResponse),

  update: async (licenceId, data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licences/${licenceId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  addFlag: async (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/feature-flags/definitions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  applyToCompany: async (companyId, licenceSlug, force = false) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licences/apply-to-company`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ company_id: companyId, licence_slug: licenceSlug, force }),
    }).then(handleResponse),


    setExtension: async (flagKey, isExtension, prixDefaut) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/feature-flags/definitions/${flagKey}/extension`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_extension: isExtension, prix_defaut: prixDefaut }),
    }).then(handleResponse),
    
},



 licenceModules: {

    getAll: async () =>
      fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-modules`, {
        headers: getAuthHeaders(),
      }).then(handleResponse),

    create: async (data) =>
      fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-modules`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }).then(handleResponse),

    update: async (key, data) =>
      fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-modules/${key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }).then(handleResponse),

    delete: async (key) =>
      fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-modules/${key}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }).then(handleResponse),

    getGrille: async () =>
      fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-modules/grille`, {
        headers: getAuthHeaders(),
      }).then(handleResponse),

    saveGrille: async (payload) =>
      fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/licence-modules/grille`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }).then(handleResponse),

  },




  features: {
    
    // Liste toutes les features
    getAll: async () => {
      console.log('📡 [SuperAdmin API] Récupération liste features...');
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/features`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Récupérer les companies pour une feature
    getCompanies: async (featureName) => {
      console.log(`📡 [SuperAdmin API] Récupération companies pour feature: ${featureName}`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/features/${featureName}/companies`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Activer pour une company
    activate: async (featureName, companyId) => {
      console.log(`📡 [SuperAdmin API] Activation feature ${featureName} pour company ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/features/activate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ featureName, companyId })
      });
      return handleResponse(response);
    },

    // Désactiver pour une company
    deactivate: async (featureName, companyId) => {
      console.log(`📡 [SuperAdmin API] Désactivation feature ${featureName} pour company ${companyId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/features/deactivate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ featureName, companyId })
      });
      return handleResponse(response);
    },

    // Activer globalement
    activateGlobal: async (featureName) => {
      console.log(`📡 [SuperAdmin API] Activation globale feature ${featureName}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/features/activate-global`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ featureName })
      });
      return handleResponse(response);
    },

    // Désactiver globalement
    deactivateGlobal: async (featureName) => {
      console.log(`📡 [SuperAdmin API] Désactivation globale feature ${featureName}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/features/deactivate-global`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ featureName })
      });
      return handleResponse(response);
    }
  },


  // ==========================================================================
  // CONFIGURATION PAGE DE CONNEXION
  // ==========================================================================
  
  loginConfig: {
    
    // Récupérer toute la config (pour l'interface admin)
    getAll: async () => {
      console.log('📡 [SuperAdmin API] Récupération config login...');
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/login-config`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },
    
    // Mettre à jour une section
    update: async (key, value) => {
      console.log(`📡 [SuperAdmin API] Mise à jour config login: ${key}`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/login-config/${key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ value })
      });
      return handleResponse(response);
    },
    
    // Upload image de fond
    uploadBackground: async (file) => {
      console.log('📡 [SuperAdmin API] Upload background login...');
      const formData = new FormData();
      formData.append('background', file);
      const token = localStorage.getItem('superadmin_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/login-config/upload-background`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      });
      return handleResponse(response);
    }
  },


  
  // ==========================================================================
  // GESTION DES COMMUNICATIONS DASHBOARD
  // ==========================================================================

  communications: {

    // Liste toutes les communications
    getAll: async () => {
      console.log('📡 [SuperAdmin API] Récupération communications...');
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Stats de lecture globales
    getStats: async () => {
      console.log('📡 [SuperAdmin API] Récupération stats communications...');
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications/stats`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Détail des lectures d'une communication
    getReads: async (communicationId) => {
      console.log(`📡 [SuperAdmin API] Récupération lectures pour communication ${communicationId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications/${communicationId}/reads`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Créer une communication
    create: async (data) => {
      console.log('📡 [SuperAdmin API] Création communication:', data.titre);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    // Modifier une communication
    update: async (communicationId, data) => {
      console.log(`📡 [SuperAdmin API] Modification communication ${communicationId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications/${communicationId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },

    // Supprimer une communication
    delete: async (communicationId) => {
      console.log(`📡 [SuperAdmin API] Suppression communication ${communicationId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications/${communicationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    },

    // Activer / Désactiver une communication
    toggle: async (communicationId) => {
      console.log(`📡 [SuperAdmin API] Toggle communication ${communicationId}...`);
      const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/communications/${communicationId}/toggle`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    }
  },



// ============================================================================




billingReminders: {

  getByCompany: async (companyId) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/billing-reminders`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateBillingDay: async (companyId, billing_day) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/billing-day`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ billing_day })
    });
    return handleResponse(response);
  },

  create: async (companyId, data) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/companies/${companyId}/billing-reminders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (reminderId, data) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/billing-reminders/${reminderId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (reminderId) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/billing-reminders/${reminderId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
},

cron: {

  getConfig: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/cron/billing`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateConfig: async (data) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/cron/billing`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  runNow: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/cron/billing/run`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  scheduleOnce: async (scheduled_once_at) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/cron/billing/schedule`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ scheduled_once_at })
    });
    return handleResponse(response);
  },

  cancelSchedule: async () => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/cron/billing/schedule`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
},


billing: {
  getPrixBase: async (companyId) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/billing/prix-base/${companyId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updatePricing: async (companyId, data) => {
    const response = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/billing/pricing/${companyId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
},

invoices: {
  getAll: async () => {
    const r = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/invoices`, { headers: getAuthHeaders() });
    return handleResponse(r);
  },
  create: async (data) => {
    const r = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/invoices`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return handleResponse(r);
  },
  markPaid: async (id) => {
    const r = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/invoices/${id}/mark-paid`, { method: 'PUT', headers: getAuthHeaders() });
    return handleResponse(r);
  },
  markWhatsappSent: async (id) => {
    const r = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/invoices/${id}/whatsapp-sent`, { method: 'PUT', headers: getAuthHeaders() });
    return handleResponse(r);
  },

  getPdf: (id) => {
  const token = localStorage.getItem('superadmin_token');
  window.open(
    `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/invoices/${id}/pdf?token=${token}`,
    '_blank'
  );
},

},

danicedSettings: {
  get: () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings`, { headers: getAuthHeaders() }).then(handleResponse),
  update: (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  getTarifs: () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/tarifs`, { headers: getAuthHeaders() }).then(handleResponse),
  updateTarifs: (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/tarifs`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  uploadLogo: async (file) => {                    // ← ICI, dans l'objet
    const formData = new FormData();
    formData.append('logo', file);
    const token = localStorage.getItem('superadmin_token');
    const res = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/upload-logo`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    return res.json();
  },

  uploadHoraPilotLogo: async (file) => {
  const formData = new FormData();
  formData.append('logo', file);
  const token = localStorage.getItem('superadmin_token');
  const res = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/upload-horapilot-logo`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  return res.json();
},


  uploadHoraPilotLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const token = localStorage.getItem('superadmin_token');
    const res = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/upload-horapilot-logo`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    return res.json();
  },
  
  uploadDocument: async (file, type) => {
    // type = 'signature' | 'cachet'
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('superadmin_token');
    const res = await fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/upload-document/${type}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    return res.json();
  },
  
  getPaymentMethods: () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/payment-methods`, { headers: getAuthHeaders() }).then(handleResponse),
  
  createPaymentMethod: (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/payment-methods`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    }).then(handleResponse),
  
  updatePaymentMethod: (id, data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/payment-methods/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    }).then(handleResponse),
  
  deletePaymentMethod: (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/daniced-settings/payment-methods/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    }).then(handleResponse),
  

},



// ── PROSPECTION (téléchargement fiches PDF) ──────────────────────────────────
prospection: {
  downloadFiche: async (type, prospectId = null) => {
    const token = localStorage.getItem('superadmin_token');
    let url = `${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospection/fiche/${type}?token=${token}`;
    if (prospectId) url += `&prospect_id=${prospectId}`;
 
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Erreur génération PDF');
 
    const blob     = await response.blob();
    const blobUrl  = window.URL.createObjectURL(blob);
    const a        = document.createElement('a');
 
    // Nom du fichier depuis le header Content-Disposition
    const disposition = response.headers.get('Content-Disposition') || '';
    const match       = disposition.match(/filename="?([^"]+)"?/);
    a.download        = match ? match[1] : `horapilot-${type}.pdf`;
 
    a.href = blobUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
 
    return { success: true };
  },
},


// ── PROSPECTS (CRM léger) ────────────────────────────────────────────────────

prospects: {
  getAll: async () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  getOne: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  create: async (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: async (id, data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  delete: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(handleResponse),

  // ── Nouvelles méthodes ──
  updateStatut: async (id, statut) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}/statut`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ statut }),
    }).then(handleResponse),

  getActions: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}/actions`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  logAction: async (id, typeAction, description, meta = null) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}/action`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ type_action: typeAction, description, meta }),
    }).then(handleResponse),

  uploadDocument: async (id, file, typeDoc, label) => {
    const formData = new FormData();
    formData.append('file', file);
    if (typeDoc) formData.append('type_doc', typeDoc);
    if (label)   formData.append('label', label);
    const token = localStorage.getItem('superadmin_token');
    return fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}/document`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    }).then(handleResponse);
  },

  deleteDocument: async (id, path) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/prospects/${id}/document`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ path }),
    }).then(handleResponse),
},

// ── DEVIS ────────────────────────────────────────────────────────────────────
devis: {
  getAll: async () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis`, { headers: getAuthHeaders() }).then(handleResponse),

  create: async (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: async (id, data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis/${id}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  markAccepted: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis/${id}/mark-accepted`, {
      method: 'PUT', headers: getAuthHeaders(),
    }).then(handleResponse),

  markWhatsappSent: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis/${id}/whatsapp-sent`, {
      method: 'PUT', headers: getAuthHeaders(),
    }).then(handleResponse),

  getPdf: (id) => {
    const token = localStorage.getItem('superadmin_token');
    window.open(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis/${id}/pdf?token=${token}`, '_blank');
  },

  markRefused: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis/${id}/mark-refused`, {
      method: 'PUT', headers: getAuthHeaders(),
    }).then(handleResponse),

  convertirContrat: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/devis/${id}/convertir-contrat`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(handleResponse),

},



contrats: {
  getAll: async () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats`, { headers: getAuthHeaders() }).then(handleResponse),

  getOne: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/${id}`, { headers: getAuthHeaders() }).then(handleResponse),

  getTemplates: async () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/articles-templates`, { headers: getAuthHeaders() }).then(handleResponse),

  create: async (data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: async (id, data) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/${id}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  markSigne: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/${id}/mark-signe`, {
      method: 'PUT', headers: getAuthHeaders(),
    }).then(handleResponse),

  delete: async (id) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    }).then(handleResponse),

  getPdf: (id) => {
    const token = localStorage.getItem('superadmin_token');
    window.open(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/${id}/pdf?token=${token}`, '_blank');
  },

  fromDevis: async (devisId) =>
     fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/contrats/from-devis/${devisId}`, {
      method: 'POST',
       headers: getAuthHeaders(),
    }).then(handleResponse),
},


extensions: {
  getAll: async () =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  getByCompany: async (companyId) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions/company/${companyId}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  activate: async (companyId, flagKey, prixMensuel) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions/activate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ company_id: companyId, flag_key: flagKey, prix_mensuel: prixMensuel }),
    }).then(handleResponse),

  updatePrice: async (companyId, flagKey, prixMensuel) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions/price`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ company_id: companyId, flag_key: flagKey, prix_mensuel: prixMensuel }),
    }).then(handleResponse),

  deactivate: async (companyId, flagKey) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions/deactivate`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ company_id: companyId, flag_key: flagKey }),
    }).then(handleResponse),

  getBilling: async (companyId) =>
    fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions/billing/${companyId}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

    getByExtension: async (flagKey) =>
  fetch(`${API_URL}/sys-mgmt-${ADMIN_ROUTE_HASH}/extensions/${flagKey}/companies`, {
    headers: getAuthHeaders(),
  }).then(handleResponse),



},






  
};

// ============================================================================
// EXPORT
// ============================================================================

export default superAdminApi;
